import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { inventoryApi, catalogApi, furnitureTypesApi } from '../services/api';
import { cacheManager } from '../services/cacheManager';
import { useAuth } from '../contexts/AuthContext';
import { useOfflineSync } from '../hooks/useOffline';
import { useCascadeLocation } from '../hooks/useCascadeLocation';
import { ClipboardList, Plus, CheckCircle, AlertCircle, ChevronRight, ScanBarcode, XCircle, MapPin, ArrowRight, BookOpen, Package, Zap, Camera, CameraOff, RefreshCw, Keyboard, WifiOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import CreateInventoryDialog from '../components/inventory/CreateInventoryDialog';
import ScanHistoryPanel from '../components/inventory/ScanHistoryPanel';
import ResultsDialog from '../components/inventory/ResultsDialog';

const STATUS_CONFIG = {
  en_cours: { label: 'En cours', className: 'bg-blue-50 text-blue-700' },
  termine: { label: 'Terminé', className: 'bg-emerald-50 text-emerald-700' }
};

const InventoryCard = ({ inventory, siteName, onScan, onViewResults, canManage }) => {
  const itemsExpected = inventory.items_expected || 0;
  const itemsScanned = inventory.items_scanned || 0;
  const progress = itemsExpected > 0 ? Math.round((itemsScanned / itemsExpected) * 100) : 0;
  const statusConfig = STATUS_CONFIG[inventory.status] || STATUS_CONFIG.en_cours;
  const isInProgress = inventory.status === 'en_cours';
  const isCompleted = inventory.status === 'termine';
  const missingCount = inventory.items_missing || 0;

  return (
    <Card className="card-hover" data-testid={`inventory-card-${inventory.id}`}>
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-heading font-bold text-lg">{inventory.nom}</h3>
              <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
            </div>
            <p className="text-sm text-slate-500">Site: {siteName}</p>
            {inventory.description && <p className="text-sm text-slate-500 mt-1">{inventory.description}</p>}
            <p className="text-xs text-slate-400 mt-2 font-mono">
              Créé le {new Date(inventory.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <div className="lg:w-64 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Progression</span>
              <span className="font-medium">{itemsScanned} / {itemsExpected}</span>
            </div>
            <Progress value={progress} className="h-2" />
            <div className="flex justify-between text-xs text-slate-400">
              <span>{progress}%</span>
              {isCompleted && missingCount > 0 && <span className="text-rose-600">{missingCount} manquant(s)</span>}
            </div>
          </div>
          <div className="flex gap-2">
            {isInProgress && canManage && (
              <Button onClick={() => onScan(inventory)} className="bg-blue-600 hover:bg-blue-700" data-testid={`scan-btn-${inventory.id}`}>
                <ScanBarcode className="w-4 h-4 mr-2" />Scanner
              </Button>
            )}
            <Button variant="outline" onClick={() => onViewResults(inventory)} data-testid={`report-btn-${inventory.id}`}>
              Rapport<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const InventoryPage = () => {
  const navigate = useNavigate();
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [inventoryResults, setInventoryResults] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [scanStatus, setScanStatus] = useState(null);
  const [formNom, setFormNom] = useState('');
  const [formSiteId, setFormSiteId] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const { isGestionnaire } = useAuth();

  // Offline sync
  const { isOnline, pendingCount, queueInventoryScan } = useOfflineSync();

  // Location cascade (extracted hook)
  const {
    sites, batiments, etages, bureaux,
    selectedSite, selectedBatiment, selectedEtage, selectedBureau,
    selectedLocationId, locationPath,
    setSelectedSite, setSelectedBatiment, setSelectedEtage, setSelectedBureau,
    loadSites,
  } = useCascadeLocation();

  const [autoTransfer, setAutoTransfer] = useState(true);
  const [autoCreate, setAutoCreate] = useState(true);
  const [autoValidate, setAutoValidate] = useState(true);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [scanMode, setScanMode] = useState('manual');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scanCooldown, setScanCooldown] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const scanInputRef = useRef(null);
  const autoValidateTimerRef = useRef(null);
  const scanHistoryRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const performScanRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  const scannerContainerId = 'inventory-scanner';
  const COOLDOWN_MS = 1500;

  // Catalog for scan
  const [catalogItems, setCatalogItems] = useState([]);
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [showCatalogSelector, setShowCatalogSelector] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [typeLabels, setTypeLabels] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [invRes, catalogRes, typesRes] = await Promise.all([
        inventoryApi.getAll(),
        catalogApi.getAll(),
        furnitureTypesApi.getAll()
      ]);
      setInventories(invRes.data);
      setCatalogItems(catalogRes.data);
      const labels = typesRes.data.reduce((acc, ft) => { acc[ft.code] = ft.label; return acc; }, {});
      setTypeLabels(labels);
      await loadSites();
    } catch (error) {
      console.error('Erreur:', error);
      try {
        const cachedInventories = await cacheManager.getCachedData('/api/inventory');
        if (cachedInventories?.data) setInventories(cachedInventories.data);

        const cachedCatalog = await cacheManager.getCachedData('/api/catalog');
        if (cachedCatalog?.data) setCatalogItems(cachedCatalog.data);

        const cachedTypes = await cacheManager.getCachedData('/api/furniture-types');
        if (cachedTypes?.data) {
          const labels = cachedTypes.data.reduce((acc, ft) => { acc[ft.code] = ft.label; return acc; }, {});
          setTypeLabels(labels);
        }
        await loadSites();
      } catch (cacheError) {
        console.error('[InventoryPage] Cache error:', cacheError);
      }
    } finally {
      setLoading(false);
    }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.nom ?? 'Inconnu';

  const getSelectedCatalogItem = () => catalogItems.find(c => c.id === selectedCatalogId);

  const filteredCatalogItems = catalogItems.filter(item => {
    if (!catalogSearch) return true;
    const search = catalogSearch.toLowerCase();
    return (
      item.marque?.toLowerCase().includes(search) ||
      item.modele?.toLowerCase().includes(search) ||
      typeLabels[item.type_mobilier]?.toLowerCase().includes(search)
    );
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await inventoryApi.create({ site_id: formSiteId, nom: formNom, description: formDescription });
      setCreateDialogOpen(false);
      setFormNom(''); setFormSiteId(''); setFormDescription('');
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const openScanDialog = (inventory) => {
    setSelectedInventory(inventory);
    setScanDialogOpen(true);
    setScanStatus(null);
    setScanHistory([]);
    setScanMode('manual');
    setCameraReady(false);
    setCameraError(null);
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const stopCameraScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) await html5QrCodeRef.current.stop();
      } catch (err) {
        console.log('Error stopping scanner:', err);
      }
      html5QrCodeRef.current = null;
    }
    setCameraReady(false);
  }, []);

  const startCameraScanner = useCallback(async () => {
    setCameraError(null);
    setCameraReady(false);
    try {
      const devices = await Html5Qrcode.getCameras();
      if (!devices || devices.length === 0) { setCameraError('Aucune caméra détectée'); return; }

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isAndroid = /Android/.test(navigator.userAgent);
      const isMobile = isIOS || isAndroid;

      let cameraToUse = devices[0].id;
      if (isMobile) {
        const rearCamera = devices.find(d =>
          d.label.toLowerCase().includes('back') ||
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        if (rearCamera) cameraToUse = rearCamera.id;
      }

      const formatsToSupport = [
        Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.DATA_MATRIX,
        Html5QrcodeSupportedFormats.AZTEC, Html5QrcodeSupportedFormats.PDF_417,
        Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A
      ];

      html5QrCodeRef.current = new Html5Qrcode(scannerContainerId, { formatsToSupport, verbose: false, useBarCodeDetectorIfSupported: true });

      await html5QrCodeRef.current.start(
        isIOS ? { facingMode: "environment" } : cameraToUse,
        { fps: 20, qrbox: { width: 300, height: 300 }, aspectRatio: 1.0, disableFlip: false, experimentalFeatures: { useBarCodeDetectorIfSupported: true } },
        (decodedText) => { if (performScanRef.current) performScanRef.current(decodedText); },
        () => {}
      );
      setCameraReady(true);
    } catch (err) {
      console.error('Camera error:', err);
      const errorStr = String(err);
      let errorMessage = "Erreur d'accès à la caméra";
      if (errorStr.includes('NotAllowedError') || errorStr.includes('Permission')) errorMessage = "Accès à la caméra refusé";
      else if (errorStr.includes('NotFoundError') || errorStr.includes('not found')) errorMessage = 'Aucune caméra détectée';
      else if (errorStr.includes('NotReadableError')) errorMessage = 'Caméra utilisée par une autre app';
      setCameraError(errorMessage);
    }
  }, []);

  const toggleScanMode = async (mode) => {
    if (mode === scanMode) return;
    if (scanMode === 'camera') await stopCameraScanner();
    setScanMode(mode);
    if (mode === 'camera') {
      setTimeout(() => startCameraScanner(), 100);
    } else {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  const closeScanDialog = async () => {
    await stopCameraScanner();
    setScanDialogOpen(false);
    setScanMode('manual');
    setScanCooldown(false);
    setCooldownRemaining(0);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
  };

  const startCooldown = useCallback(() => {
    setScanCooldown(true);
    setCooldownRemaining(COOLDOWN_MS);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    const startTime = Date.now();
    cooldownTimerRef.current = setInterval(() => {
      const remaining = Math.max(0, COOLDOWN_MS - (Date.now() - startTime));
      setCooldownRemaining(remaining);
      if (remaining <= 0) { clearInterval(cooldownTimerRef.current); setScanCooldown(false); setCooldownRemaining(0); }
    }, 100);
  }, []);

  const playBeep = (frequency, duration) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = frequency; osc.type = 'sine'; gain.gain.value = 0.1;
      osc.start(); setTimeout(() => osc.stop(), duration);
    } catch (e) { /* Audio not supported */ }
  };

  const performScan = useCallback(async (code) => {
    if (!code || !selectedInventory || scanCooldown) return;
    try {
      const scanData = {
        code_barres: code,
        location_id: selectedLocationId,
        create_transfer: autoTransfer,
        catalog_id: selectedCatalogId || null,
        create_if_not_found: autoCreate
      };

      if (!isOnline) {
        await queueInventoryScan(selectedInventory.id, scanData);
        const entry = { id: Date.now(), code, status: 'offline', message: 'En attente de synchronisation', timestamp: new Date(), isOffline: true };
        setScanHistory(prev => [entry, ...prev]);
        setScanStatus({ success: true, message: 'Scan enregistré (hors ligne)', isOffline: true });
        toast.info('Scan enregistré hors ligne', { description: 'Sera synchronisé au retour en ligne' });
      } else {
        const response = await inventoryApi.scan(selectedInventory.id, scanData);
        const { is_extra: isExtra, transfer_created: transferCreated, furniture_created: furnitureCreated } = response.data;

        let message = furnitureCreated ? 'Nouveau mobilier créé' : isExtra ? 'Mobilier non attendu sur ce site' : 'Mobilier trouvé';
        let status = furnitureCreated ? 'created' : isExtra ? 'extra' : 'found';
        if (transferCreated) { message += ' - Transfert créé'; status = 'transfer'; }

        const entry = {
          id: Date.now(), code, status, message,
          furniture: response.data.furniture, isExtra: isExtra && !furnitureCreated,
          transferCreated, furnitureCreated,
          oldLocation: response.data.old_location, newLocation: response.data.new_location,
          timestamp: new Date()
        };
        setScanHistory(prev => [entry, ...prev]);
        setScanStatus({ success: true, message, furniture: response.data.furniture, isExtra: isExtra && !furnitureCreated, transferCreated, furnitureCreated, oldLocation: response.data.old_location, newLocation: response.data.new_location });
        loadData();
        playBeep(1000, 100);
      }

      setTimeout(() => { if (scanHistoryRef.current) scanHistoryRef.current.scrollTop = 0; }, 50);
      setScanInput('');
      startCooldown();
      setTimeout(() => scanInputRef.current?.focus(), 100);
    } catch (error) {
      if (!navigator.onLine || error.message === 'Network Error') {
        await queueInventoryScan(selectedInventory.id, { code_barres: code, location_id: selectedLocationId, create_transfer: autoTransfer, catalog_id: selectedCatalogId || null, create_if_not_found: autoCreate });
        const entry = { id: Date.now(), code, status: 'offline', message: 'En attente de synchronisation', timestamp: new Date(), isOffline: true };
        setScanHistory(prev => [entry, ...prev]);
        setScanStatus({ success: true, message: 'Scan enregistré (hors ligne)', isOffline: true });
        toast.info('Scan enregistré hors ligne');
      } else {
        const entry = { id: Date.now(), code, status: 'error', message: error.response?.data?.detail || 'Erreur lors du scan', timestamp: new Date() };
        setScanHistory(prev => [entry, ...prev]);
        setScanStatus({ success: false, message: error.response?.data?.detail || 'Erreur lors du scan' });
        playBeep(200, 300);
      }
      setScanInput('');
      startCooldown();
    }
  }, [selectedInventory, autoTransfer, autoCreate, selectedCatalogId, scanCooldown, startCooldown, isOnline, queueInventoryScan, selectedLocationId]);

  useEffect(() => { performScanRef.current = performScan; }, [performScan]);

  const handleScanInputChange = (e) => {
    const value = e.target.value;
    setScanInput(value);
    if (scanCooldown) return;
    if (autoValidateTimerRef.current) clearTimeout(autoValidateTimerRef.current);
    if (autoValidate && value.trim()) {
      autoValidateTimerRef.current = setTimeout(() => performScan(value.trim()), 300);
    }
  };

  const handleScanKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (autoValidateTimerRef.current) clearTimeout(autoValidateTimerRef.current);
      performScan(scanInput.trim());
    }
  };

  const handleScan = (e) => {
    e.preventDefault();
    if (autoValidateTimerRef.current) clearTimeout(autoValidateTimerRef.current);
    performScan(scanInput.trim());
  };

  const handleComplete = async () => {
    if (!selectedInventory) return;
    try {
      await inventoryApi.complete(selectedInventory.id);
      setScanDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const viewResults = (inventory) => navigate(`/inventory/${inventory.id}/report`);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  const canManage = isGestionnaire();
  const invScanned = selectedInventory?.items_scanned ?? 0;
  const invExpected = selectedInventory?.items_expected ?? 0;
  const invProgress = invExpected > 0 ? (invScanned / invExpected) * 100 : 0;
  const selectedCatalog = getSelectedCatalogItem();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Inventaire</h1>
            {!isOnline && (
              <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
                <WifiOff className="w-3 h-3" />Hors ligne
              </Badge>
            )}
            {pendingCount > 0 && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <RefreshCw className="w-3 h-3" />{pendingCount} en attente
              </Badge>
            )}
          </div>
          <p className="text-slate-500 mt-1">Gérez vos campagnes d&apos;inventaire physique</p>
        </div>
        {canManage && (
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700 btn-scale" data-testid="new-inventory-btn">
            <Plus className="w-4 h-4 mr-2" />Nouvel inventaire
          </Button>
        )}
      </div>

      {inventories.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Aucun inventaire</h3>
            <p className="text-slate-500 mt-1">Créez votre premier inventaire pour commencer</p>
            {canManage && (
              <Button onClick={() => setCreateDialogOpen(true)} className="mt-4 bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />Créer un inventaire
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {inventories.map((inv) => (
            <InventoryCard key={inv.id} inventory={inv} siteName={getSiteName(inv.site_id)} onScan={openScanDialog} onViewResults={viewResults} canManage={canManage} />
          ))}
        </div>
      )}

      <ScanHistoryPanel scanHistory={scanHistory} onClear={() => setScanHistory([])} />

      <CreateInventoryDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        sites={sites}
        formNom={formNom} setFormNom={setFormNom}
        formSiteId={formSiteId} setFormSiteId={setFormSiteId}
        formDescription={formDescription} setFormDescription={setFormDescription}
        onSubmit={handleCreate}
      />

      {/* Scan Dialog */}
      <Dialog open={scanDialogOpen} onOpenChange={(open) => !open && closeScanDialog()}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Scanner - {selectedInventory?.nom}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Progress */}
            <div className="p-4 rounded-lg bg-slate-50">
              <div className="flex justify-between text-sm mb-2">
                <span>Progression</span>
                <span className="font-medium">{invScanned} / {invExpected}</span>
              </div>
              <Progress value={invProgress} className="h-2" />
            </div>

            {/* Catalog Selector */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => setShowCatalogSelector(!showCatalogSelector)} className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-600">
                  <BookOpen className="w-4 h-4" />Catalogue (si code inconnu)
                </button>
                <div className="flex items-center gap-2">
                  <Label htmlFor="auto-create" className="text-xs text-slate-500">Créer auto</Label>
                  <Switch id="auto-create" checked={autoCreate} onCheckedChange={setAutoCreate} data-testid="auto-create-switch" />
                </div>
              </div>
              {selectedCatalog && (
                <div className="flex items-center gap-3 text-sm bg-purple-50 px-3 py-2 rounded">
                  <Package className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-purple-700">{selectedCatalog.marque} {selectedCatalog.modele}</span>
                  <Badge variant="outline" className="text-xs">{typeLabels[selectedCatalog.type_mobilier] || selectedCatalog.type_mobilier}</Badge>
                  <button type="button" onClick={() => setSelectedCatalogId('')} className="ml-auto text-slate-400 hover:text-slate-600">×</button>
                </div>
              )}
              {showCatalogSelector && (
                <div className="space-y-2 pt-2">
                  <Input placeholder="Rechercher dans le catalogue..." value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)} className="h-9" />
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {filteredCatalogItems.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-2">Aucun article dans le catalogue</p>
                    ) : filteredCatalogItems.map(item => (
                      <button key={item.id} type="button"
                        onClick={() => { setSelectedCatalogId(item.id); setShowCatalogSelector(false); }}
                        className={`w-full flex items-center gap-2 p-2 rounded text-left text-sm hover:bg-slate-100 ${selectedCatalogId === item.id ? 'bg-purple-50 border border-purple-200' : ''}`}
                      >
                        <Package className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="font-medium truncate">{item.marque}</span>
                        <span className="text-slate-500 truncate">{item.modele}</span>
                        <Badge variant="outline" className="text-xs ml-auto">{typeLabels[item.type_mobilier] || item.type_mobilier}</Badge>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Location Selector */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => setShowLocationSelector(!showLocationSelector)} className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-600">
                  <MapPin className="w-4 h-4" />Localisation du scan
                </button>
                <div className="flex items-center gap-2">
                  <Label htmlFor="auto-transfer" className="text-xs text-slate-500">Transfert auto</Label>
                  <Switch id="auto-transfer" checked={autoTransfer} onCheckedChange={setAutoTransfer} data-testid="auto-transfer-switch" />
                </div>
              </div>
              {locationPath && <div className="text-sm text-blue-600 font-medium bg-blue-50 px-3 py-2 rounded">{locationPath}</div>}
              {showLocationSelector && (
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {[
                    { label: 'Site', items: sites, value: selectedSite, setter: setSelectedSite, testId: 'scan-select-site' },
                    { label: 'Bâtiment', items: batiments, value: selectedBatiment, setter: setSelectedBatiment, testId: 'scan-select-batiment', disabled: !selectedSite || batiments.length === 0 },
                    { label: 'Étage', items: etages, value: selectedEtage, setter: setSelectedEtage, testId: 'scan-select-etage', disabled: !selectedBatiment || etages.length === 0 },
                    { label: 'Bureau', items: bureaux, value: selectedBureau, setter: setSelectedBureau, testId: 'scan-select-bureau', disabled: !selectedEtage || bureaux.length === 0 },
                  ].map(({ label, items, value, setter, testId, disabled }) => (
                    <div key={label} className="space-y-1">
                      <Label className="text-xs">{label}</Label>
                      <Select value={value} onValueChange={setter} disabled={disabled}>
                        <SelectTrigger className="h-9" data-testid={testId}>
                          <SelectValue placeholder={items.length === 0 ? '-' : label} />
                        </SelectTrigger>
                        <SelectContent>
                          {items.map(item => <SelectItem key={item.id} value={item.id}>{item.nom}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Scan Mode Toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
              {[
                { mode: 'manual', icon: <Keyboard className="w-4 h-4" />, label: 'Saisie / Lecteur' },
                { mode: 'camera', icon: <Camera className="w-4 h-4" />, label: 'Caméra' },
              ].map(({ mode, icon, label }) => (
                <button key={mode} type="button" onClick={() => toggleScanMode(mode)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${scanMode === mode ? 'bg-white shadow text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  {icon}{label}
                </button>
              ))}
            </div>

            {/* Camera Scanner */}
            {scanMode === 'camera' && (
              <div className="border rounded-lg overflow-hidden bg-black">
                {cameraError ? (
                  <div className="flex flex-col items-center justify-center p-8 text-white">
                    <CameraOff className="w-12 h-12 text-slate-400 mb-3" />
                    <p className="text-center mb-4">{cameraError}</p>
                    <Button onClick={startCameraScanner} variant="outline" className="text-white border-white hover:bg-white/10">
                      <RefreshCw className="w-4 h-4 mr-2" />Réessayer
                    </Button>
                  </div>
                ) : (
                  <div className="relative" style={{ minHeight: '200px' }}>
                    <div id={scannerContainerId} style={{ width: '100%' }}></div>
                    {!cameraReady && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 text-white">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mb-3"></div>
                        <p className="text-sm">Initialisation caméra...</p>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-center text-slate-400 py-2 bg-slate-900">Pointez vers un code-barres (Code 128, QR, EAN...)</p>
              </div>
            )}

            {/* Manual Input Mode */}
            {scanMode === 'manual' && (
              <>
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Validation automatique (lecteur)</span>
                  </div>
                  <Switch id="auto-validate" checked={autoValidate} onCheckedChange={setAutoValidate} data-testid="auto-validate-switch" />
                </div>
                <form onSubmit={handleScan} className="flex gap-2">
                  <Input
                    ref={scanInputRef}
                    value={scanInput}
                    onChange={handleScanInputChange}
                    onKeyDown={handleScanKeyDown}
                    placeholder={autoValidate ? "Scannez avec le lecteur (auto)..." : "Scannez ou saisissez le code-barres..."}
                    className="font-mono"
                    autoFocus
                    data-testid="scan-input"
                  />
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" title="Valider manuellement">
                    <ScanBarcode className="w-4 h-4" />
                  </Button>
                </form>
              </>
            )}

            {/* Last Scan Status */}
            {scanStatus && (
              <div className={`flex items-start gap-3 p-4 rounded-lg ${
                scanStatus.success
                  ? (scanStatus.furnitureCreated ? 'bg-purple-50 text-purple-700' : (scanStatus.isExtra ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'))
                  : 'bg-rose-50 text-rose-700'
              }`}>
                {scanStatus.success
                  ? (scanStatus.furnitureCreated ? <Plus className="w-5 h-5 flex-shrink-0 mt-0.5" /> : (scanStatus.isExtra ? <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />))
                  : <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <p className="font-medium">{scanStatus.message}</p>
                  {scanStatus.furniture && <p className="text-sm opacity-75 mt-1">{scanStatus.furniture.marque} {scanStatus.furniture.modele}</p>}
                  {(scanStatus.transferCreated || scanStatus.furnitureCreated) && scanStatus.newLocation && (
                    <div className="flex items-center gap-2 text-xs mt-2 bg-white/50 px-2 py-1 rounded">
                      {scanStatus.transferCreated && <span className="text-slate-600">{scanStatus.oldLocation || 'Non assigné'}</span>}
                      {scanStatus.transferCreated && <ArrowRight className="w-3 h-3" />}
                      <span className="font-medium">{scanStatus.newLocation}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {scanHistory.length > 0 && (
              <p className="text-xs text-center text-slate-500">
                L&apos;historique des {scanHistory.length} scan(s) est visible sur la page en arrière-plan
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeScanDialog}>Fermer</Button>
            <Button onClick={handleComplete} className="bg-emerald-600 hover:bg-emerald-700" data-testid="complete-inventory-btn">
              <CheckCircle className="w-4 h-4 mr-2" />Terminer l&apos;inventaire
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResultsDialog
        open={resultsDialogOpen}
        onOpenChange={setResultsDialogOpen}
        inventoryResults={inventoryResults}
      />
    </div>
  );
};

export default InventoryPage;
